import type { GuidanceLayoutHint, GuidanceNodeStatus, GuidanceTreeNode, GuidanceWeekRange } from './types';
import type { FlatGuidanceRow } from './derivation';
import { barGeometry, guidanceLayoutConstants } from './derivation';
import { clipBarGeometryInViewport, GUIDANCE_AXIS_BASE_DATE, guidanceTodayWeek } from './guidanceViewport';

export { guidanceTodayWeek };

export const GUIDANCE_STATUS_COLORS: Record<
  GuidanceNodeStatus,
  { track: string; fill: string; border: string; stripe: string; dot: string }
> = {
  done: {
    track: '#dcfce7',
    fill: '#00d455',
    border: '#16a34a',
    stripe: 'rgba(22,163,74,.40)',
    dot: '#16a34a',
  },
  active: {
    track: '#dbeafe',
    fill: '#0066ff',
    border: '#2563eb',
    stripe: 'rgba(0,102,255,.42)',
    dot: '#0066ff',
  },
  todo: {
    track: '#f3f4f6',
    fill: '#9ca3af',
    border: '#cbd1da',
    stripe: 'rgba(100,116,139,.40)',
    dot: '#cbd1da',
  },
};

export const GUIDANCE_INDENT_PX = 17;

export interface GuidanceRowGeometry {
  nodeId: string;
  left: number;
  earlyWidth: number;
  tailWidth: number;
  y: number;
  sx: number;
  exEarly: number;
  ex: number;
}

export function buildGuidanceRowGeometries(
  rows: FlatGuidanceRow[],
  weekWidth = guidanceLayoutConstants().weekWidthPx,
  viewportStartWeek = 0,
  viewportWeekCount?: number
): GuidanceRowGeometry[] {
  const { rowHeightPx } = guidanceLayoutConstants();
  return rows.map((row, index) => {
    const geom =
      viewportStartWeek === 0
        ? barGeometry(row.node.range, weekWidth)
        : clipBarGeometryInViewport(
            row.node.range,
            weekWidth,
            viewportStartWeek,
            viewportWeekCount ?? 12
          );
    const y = index * rowHeightPx + rowHeightPx / 2;
    return {
      nodeId: row.node.id,
      left: geom.left,
      earlyWidth: geom.earlyWidth,
      tailWidth: geom.tailWidth,
      y,
      sx: geom.left,
      exEarly: geom.left + geom.earlyWidth,
      ex: geom.left + geom.earlyWidth + geom.tailWidth,
    };
  });
}

export function guidanceNameMap(root: GuidanceTreeNode): Record<string, string> {
  const map: Record<string, string> = {};
  const walk = (n: GuidanceTreeNode) => {
    map[n.id] = n.label;
    (n.children ?? []).forEach(walk);
  };
  walk(root);
  return map;
}

/** Collapse whitespace for duplicate title/sublabel comparison. */
export function normalizeGuidanceDisplayText(text: string): string {
  return text.trim().replace(/\s+/g, ' ');
}

export function guidanceDisplayTextEquals(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  return normalizeGuidanceDisplayText(a) === normalizeGuidanceDisplayText(b);
}

export function looksLikeGuidanceMachineryId(text: string): boolean {
  const t = text.trim();
  if (!t) return true;
  if (/^(ca-|co-|pt-|ai-|owi-|wp-|imp-)/i.test(t)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(t)) return true;
  if (/^[a-z0-9]+(?:-[a-z0-9]+)+$/.test(t)) return true;
  return false;
}

export function formatGuidanceCounts(counts: {
  done: number;
  open: number;
  eligible: number;
}): string {
  return `${counts.done} done · ${counts.open} open · ${counts.eligible} ready`;
}

export function guidanceSublabel(
  node: GuidanceTreeNode,
  nameById: Record<string, string>,
  sessionLabel: string,
  objectiveLabel?: string
): { text: string; mono: boolean } | null {
  if (node.type === 'session') {
    const obj = (objectiveLabel ?? '').trim();
    if (!obj || looksLikeGuidanceMachineryId(obj) || guidanceDisplayTextEquals(obj, node.label)) {
      return null;
    }
    return { text: obj, mono: false };
  }
  if (node.type === 'waypoint' && node.artifact) {
    if (guidanceDisplayTextEquals(node.artifact, node.label)) return null;
    if (looksLikeGuidanceMachineryId(node.artifact)) return null;
    return { text: node.artifact, mono: false };
  }
  if (node.type === 'improvement') {
    const parts: string[] = [];
    if (node.futureCondition) parts.push(`Future: ${node.futureCondition}`);
    if (node.assumptionText) parts.push(`Assumption: ${node.assumptionText}`);
    if (node.plankDescription && !guidanceDisplayTextEquals(node.plankDescription, node.label)) {
      parts.push(node.plankDescription);
    }
    if (node.resourceAssignment) parts.push(`Owner: ${node.resourceAssignment}`);
    const schedule: string[] = [];
    if (node.startDate) schedule.push(`Start: ${node.startDate}`);
    if (node.endDate) schedule.push(`End: ${node.endDate}`);
    const durationLabel = node.estimatedDuration ?? node.duration;
    if (durationLabel) schedule.push(`Duration: ${durationLabel}`);
    if (schedule.length) parts.push(schedule.join(' · '));
    return parts.length ? { text: parts.join(' · '), mono: false } : null;
  }
  if (node.type === 'activity') {
    const parts = [node.res ?? ''].filter(Boolean);
    if (node.dep && nameById[node.dep]) {
      parts.push(`after ${nameById[node.dep]}`);
    }
    return parts.length ? { text: parts.join(' · '), mono: false } : null;
  }
  return null;
}

export interface SvgPathSpec {
  id: string;
  d: string;
  stroke: string;
  strokeWidth: number;
  markerEnd?: string;
  dashed?: boolean;
}

export function buildDependencyPaths(
  rows: FlatGuidanceRow[],
  geomById: Record<string, GuidanceRowGeometry>,
  treeWidth: number
): SvgPathSpec[] {
  const paths: SvgPathSpec[] = [];
  for (const row of rows) {
    const n = row.node;
    if (n.type !== 'activity' || !n.dep) continue;
    const parent = geomById[n.dep];
    const child = geomById[n.id];
    if (!parent || !child) continue;
    const x1 = parent.exEarly - treeWidth;
    const x2 = child.sx - treeWidth;
    const mx = x2 > x1 ? Math.max(x1 + 6, x2 - 10) : (x1 + x2) / 2;
    paths.push({
      id: `dep-${n.id}`,
      d: `M ${x1} ${parent.y} H ${mx} V ${child.y} H ${x2}`,
      stroke: '#c2cad6',
      strokeWidth: 1.4,
      markerEnd: 'depArrow',
    });
  }
  return paths;
}

export function buildCriticalPathSegments(
  rows: FlatGuidanceRow[],
  geomById: Record<string, GuidanceRowGeometry>,
  treeWidth: number
): SvgPathSpec[] {
  const critNodes = rows
    .filter((r) => r.node.critical && !(r.isCollapsible && r.isCollapsed))
    .map((r) => geomById[r.node.id])
    .filter(Boolean) as GuidanceRowGeometry[];

  const paths: SvgPathSpec[] = [];
  for (let i = 0; i < critNodes.length - 1; i += 1) {
    const a = critNodes[i]!;
    const b = critNodes[i + 1]!;
    const x1 = a.exEarly - treeWidth;
    const x2 = b.sx - treeWidth;
    const mx = x2 > x1 ? Math.max(x1 + 6, x2 - 10) : (x1 + x2) / 2;
    paths.push({
      id: `crit-${i}`,
      d: `M ${x1} ${a.y} H ${mx} V ${b.y} H ${x2}`,
      stroke: '#ef4444',
      strokeWidth: 2.4,
      markerEnd: 'critArrow',
    });
  }
  return paths;
}

export function weekAxisLabels(
  weekCount: number,
  options: { startWeek?: number; todayWeek?: number } = {}
): string[] {
  const startWeek = options.startWeek ?? 0;
  const todayWeek = options.todayWeek ?? Math.floor(guidanceTodayWeek());
  return Array.from({ length: weekCount }, (_, i) => {
    const weekIndex = startWeek + i;
    const d = new Date(GUIDANCE_AXIS_BASE_DATE.getTime() + weekIndex * 7 * 86400000);
    const label = `${d.getMonth() + 1}/${d.getDate()}`;
    return weekIndex === todayWeek ? `${label} · today` : label;
  });
}

export function showsCommittedMark(node: GuidanceTreeNode): boolean {
  return (node.type === 'session' || node.type === 'waypoint') && node.range.l > node.range.e;
}

export function projectedPx(range: GuidanceWeekRange, weekWidth: number, viewportStart = 0): number {
  const week = range.e + 0.5 * (range.l - range.e);
  return (week - viewportStart) * weekWidth;
}

export function committedPx(range: GuidanceWeekRange, weekWidth: number, viewportStart = 0): number {
  const week = range.e + 0.85 * (range.l - range.e);
  return (week - viewportStart) * weekWidth;
}

export interface GuidanceGlanceTile {
  key: 'paths' | 'zero-float' | 'unestimated';
  question: string;
  value: number;
}

function collectGuidanceNodes(
  node: GuidanceTreeNode,
  type: GuidanceTreeNode['type'],
  out: GuidanceTreeNode[]
): void {
  if (node.type === type) out.push(node);
  for (const child of node.children ?? []) collectGuidanceNodes(child, type, out);
}

function collectGuidanceActivities(node: GuidanceTreeNode, out: GuidanceTreeNode[]): void {
  collectGuidanceNodes(node, 'activity', out);
}

/** One path per chain end. Independent activities are separate paths. */
function guidancePathEnds(activities: GuidanceTreeNode[]): GuidanceTreeNode[] {
  const dependedOn = new Set(
    activities.map((node) => node.dep).filter((dep): dep is string => Boolean(dep))
  );
  const ends = activities.filter((node) => !dependedOn.has(node.id));
  return ends.length > 0 ? ends : activities;
}

function guidanceChainHasUnknownFloat(
  end: GuidanceTreeNode,
  byId: Map<string, GuidanceTreeNode>
): boolean {
  const seen = new Set<string>();
  let current: GuidanceTreeNode | undefined = end;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    if (guidanceActivityFloatWeeks(current) == null) return true;
    current = current.dep ? byId.get(current.dep) : undefined;
  }
  return false;
}

export function guidanceGlanceTiles(root: GuidanceTreeNode): GuidanceGlanceTile[] {
  const activities: GuidanceTreeNode[] = [];
  collectGuidanceActivities(root, activities);
  const byId = new Map(activities.map((node) => [node.id, node]));
  const ends = guidancePathEnds(activities);
  const unestimatedPaths = ends.filter((end) => guidanceChainHasUnknownFloat(end, byId)).length;
  return [
    {
      key: 'paths',
      question: 'Which one is critical?',
      value: ends.length,
    },
    {
      key: 'zero-float',
      question: 'Who holds the clock?',
      value: activities.filter((node) => guidanceActivityFloatWeeks(node) === 0).length,
    },
    {
      key: 'unestimated',
      question: 'Could it become critical?',
      value: unestimatedPaths,
    },
  ];
}

/** Null when the activity has no duration estimate. Zero when early and late finish match. */
export function guidanceActivityFloatWeeks(node: GuidanceTreeNode): number | null {
  const hasEstimate = Boolean(node.estimatedDuration || node.duration);
  const rangeSet = node.range.l !== node.range.e || node.range.s !== 0 || node.range.e !== 0;
  if (!hasEstimate && !rangeSet) return null;
  if (!hasEstimate && node.range.l === node.range.e && node.range.s === 0) return null;
  return node.range.l - node.range.e;
}

/** Zero float, then unknown float, then the rest ascending. */
export function guidanceActivitiesByFloat(root: GuidanceTreeNode): GuidanceTreeNode[] {
  const activities: GuidanceTreeNode[] = [];
  collectGuidanceActivities(root, activities);
  const rank = (node: GuidanceTreeNode): [number, number] => {
    const floatWeeks = guidanceActivityFloatWeeks(node);
    if (floatWeeks === 0) return [0, 0];
    if (floatWeeks == null) return [1, 0];
    return [2, floatWeeks];
  };
  return [...activities].sort((a, b) => {
    const [ra, fa] = rank(a);
    const [rb, fb] = rank(b);
    if (ra !== rb) return ra - rb;
    return fa - fb;
  });
}

/** Waypoints when the tree has them; otherwise activities. Zero, unknown, then ascending float. */
export function guidanceRowsByFloat(root: GuidanceTreeNode): GuidanceTreeNode[] {
  const waypoints: GuidanceTreeNode[] = [];
  collectGuidanceNodes(root, 'waypoint', waypoints);
  if (waypoints.length === 0) return guidanceActivitiesByFloat(root);
  const rank = (node: GuidanceTreeNode): [number, number] => {
    const floatWeeks = guidanceActivityFloatWeeks(node);
    if (floatWeeks === 0) return [0, 0];
    if (floatWeeks == null) return [1, 0];
    return [2, floatWeeks];
  };
  return [...waypoints].sort((a, b) => {
    const [ra, fa] = rank(a);
    const [rb, fb] = rank(b);
    if (ra !== rb) return ra - rb;
    return fa - fb;
  });
}

export interface GuidanceFloatRow {
  id: string;
  label: string;
  floatWeeks: number | null;
  waitingOn: string;
  secondQuestion: string;
  secondAnswer: string;
}

function dedupeGuidanceNodes(nodes: GuidanceTreeNode[]): GuidanceTreeNode[] {
  const seen = new Set<string>();
  const out: GuidanceTreeNode[] = [];
  nodes.forEach((node, index) => {
    const id = node.id.trim() || `row-${index}`;
    if (seen.has(id)) return;
    seen.add(id);
    out.push(node.id.trim() ? node : { ...node, id });
  });
  return out;
}

function descendantActivities(node: GuidanceTreeNode): GuidanceTreeNode[] {
  const activities: GuidanceTreeNode[] = [];
  collectGuidanceActivities(node, activities);
  if (node.type === 'activity' && !activities.some((row) => row.id === node.id)) {
    activities.unshift(node);
  }
  return activities;
}

function firstOpen(nodes: GuidanceTreeNode[]): GuidanceTreeNode | undefined {
  return nodes.find((node) => node.status !== 'done');
}

function activityClockLabel(node: GuidanceTreeNode): string {
  const owner = node.res?.trim() || node.resourceAssignment?.trim();
  const range =
    node.estimatedDuration?.trim() ||
    node.duration?.trim() ||
    (node.startDate && node.endDate ? `${node.startDate}–${node.endDate}` : '');
  return [node.label, owner, range].filter(Boolean).join(' · ');
}

function waitingOnLabel(
  node: GuidanceTreeNode,
  activities: GuidanceTreeNode[],
  byId: Map<string, GuidanceTreeNode>
): string {
  const blocked = activities.find((row) => row.blocker && row.status !== 'done');
  if (blocked) return blocked.blockerTitle?.trim() || blocked.label;
  const openActivity =
    node.type === 'activity'
      ? firstOpen(activities)
      : firstOpen(activities.filter((row) => row.id !== node.id));
  if (openActivity?.dep) {
    const predecessor = byId.get(openActivity.dep);
    if (predecessor && predecessor.status !== 'done' && predecessor.label.trim()) {
      return predecessor.label;
    }
  }
  if (openActivity?.label.trim()) return openActivity.label;
  const improvements: GuidanceTreeNode[] = [];
  collectGuidanceNodes(node, 'improvement', improvements);
  const openImprovement = firstOpen(improvements.filter((row) => row.id !== node.id));
  if (openImprovement?.label.trim()) return openImprovement.label;
  return 'Nothing named';
}

function floatRank(floatWeeks: number | null): [number, number] {
  if (floatWeeks === 0) return [0, 0];
  if (floatWeeks == null) return [1, 0];
  return [2, floatWeeks];
}

/**
 * One row per waypoint (or per activity when the tree has no waypoints).
 * Answers use that node's improvements and activities. Call this before the
 * timeline drops those children.
 */
export function guidanceFloatRows(root: GuidanceTreeNode): GuidanceFloatRow[] {
  const waypoints: GuidanceTreeNode[] = [];
  collectGuidanceNodes(root, 'waypoint', waypoints);
  const sources =
    waypoints.length > 0 ? dedupeGuidanceNodes(waypoints) : dedupeGuidanceNodes(guidanceActivitiesByFloat(root));
  const allActivities: GuidanceTreeNode[] = [];
  collectGuidanceActivities(root, allActivities);
  const byId = new Map(allActivities.map((node) => [node.id, node]));

  const rows = sources.map((node) => {
    const activities = descendantActivities(node);
    const known = activities
      .map((row) => guidanceActivityFloatWeeks(row))
      .filter((value): value is number => value != null);
    const floatWeeks = known.length > 0 ? Math.min(...known) : guidanceActivityFloatWeeks(node);
    const zeroFloat = activities.filter((row) => guidanceActivityFloatWeeks(row) === 0);
    const starter = firstOpen(activities) ?? activities[0];
    const starts = node.status === 'todo';
    return {
      id: node.id,
      label: node.label,
      floatWeeks,
      waitingOn: waitingOnLabel(node, activities, byId),
      secondQuestion: starts ? 'What starts it?' : 'Which activities have zero float?',
      secondAnswer: starts
        ? starter?.label.trim() || 'Nothing named'
        : zeroFloat.map(activityClockLabel).join(', ') || 'None',
    };
  });

  return rows.sort((a, b) => {
    const [ra, fa] = floatRank(a.floatWeeks);
    const [rb, fb] = floatRank(b.floatWeeks);
    if (ra !== rb) return ra - rb;
    return fa - fb;
  });
}

export interface GuidanceDagNodeView {
  id: string;
  label: string;
  column: number;
  critical: boolean;
  floatUnknown: boolean;
  isEnd: boolean;
  expectedRange?: string;
}

export interface GuidanceDagEdgeView {
  from: string;
  to: string;
  critical: boolean;
}

function waypointById(root: GuidanceTreeNode): Map<string, GuidanceTreeNode> {
  const out = new Map<string, GuidanceTreeNode>();
  const walk = (node: GuidanceTreeNode) => {
    if (node.type === 'waypoint') out.set(node.id, node);
    for (const child of node.children ?? []) walk(child);
  };
  walk(root);
  return out;
}

function waypointIsCritical(node: GuidanceTreeNode | undefined): boolean {
  if (!node || node.floatUnknown) return false;
  if (node.floatWeeks === 0) return true;
  return node.range.l === node.range.e && node.range.e !== node.range.s;
}

/** Render model for a server layout_hint. Returns null when the hint has no columns. */
export function guidanceDagFromLayout(
  root: GuidanceTreeNode,
  hint: GuidanceLayoutHint | null | undefined
): { nodes: GuidanceDagNodeView[]; edges: GuidanceDagEdgeView[] } | null {
  const columns = hint?.columns?.filter((column) => column.length > 0) ?? [];
  if (columns.length === 0) return null;
  const byId = waypointById(root);
  const endId = hint?.endNodeId || columns[columns.length - 1][columns[columns.length - 1].length - 1];
  const nodes: GuidanceDagNodeView[] = [];
  columns.forEach((column, columnIndex) => {
    column.forEach((id) => {
      const node = byId.get(id);
      if (!node) return;
      nodes.push({
        id,
        label: node.label,
        column: columnIndex,
        critical: waypointIsCritical(node),
        floatUnknown: node.floatUnknown === true,
        isEnd: id === endId,
        expectedRange: id === endId ? hint?.expectedRange : undefined,
      });
    });
  });
  if (nodes.length === 0) return null;
  const criticalById = new Map(nodes.map((node) => [node.id, node.critical]));
  const edges: GuidanceDagEdgeView[] = (hint?.edges ?? []).map((edge) => {
    const from = edge[0];
    const to = edge[1];
    return {
      from,
      to,
      critical: Boolean(criticalById.get(from) && criticalById.get(to)),
    };
  });
  return { nodes, edges };
}
